import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getVeeqoStatus, triggerVeeqoSync } from '../../services/adminApi';
import { Button } from '../ui/button';
import { RefreshCw, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';

const VeeqoSyncStatus = ({ type = 'inventory' }) => {
    const [status, setStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const intervalRef = useRef(null);

    const isProductSync = type === 'products';
    const isSyncActive = status?.active > 0;
    const progress = status?.progress; // { synced, total, status }

    const fetchStatus = useCallback(async () => {
        try {
            const response = await getVeeqoStatus();
            if (response.success) {
                setStatus(response.status);
                // Clear syncing state once queue goes idle
                if (response.status.active === 0 && response.status.waiting === 0) {
                    setSyncing(false);
                }
            }
        } catch (error) {
            console.error('Failed to fetch Veeqo status:', error);
        }
    }, []);

    // Poll fast (2s) while active, slow (30s) when idle
    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        fetchStatus();
        const delay = isSyncActive || syncing ? 2000 : 30000;
        intervalRef.current = setInterval(fetchStatus, delay);

        return () => clearInterval(intervalRef.current);
    }, [fetchStatus, isSyncActive, syncing]);

    const handleManualSync = async () => {
        setSyncing(true);
        try {
            const response = await triggerVeeqoSync(type);
            if (response.success) {
                toast.success(response.message);
                setTimeout(fetchStatus, 500);
            } else {
                toast.error(response.error || 'Failed to trigger sync');
                setSyncing(false);
            }
        } catch (error) {
            toast.error('An error occurred while triggering sync');
            setSyncing(false);
        }
    };

    const isIdle = status && status.waiting === 0 && status.active === 0;
    const showProgress = isProductSync && isSyncActive && progress;
    const progressPct = showProgress ? Math.round((progress.synced / progress.total) * 100) : 0;

    return (
        <div className="bg-white rounded-lg border shadow-sm mb-4 overflow-hidden">
            <div className="flex items-center space-x-4 p-3">
                <div className="flex items-center space-x-2 shrink-0">
                    <RefreshCw className={`h-4 w-4 text-gray-400 ${isSyncActive || syncing ? 'animate-spin' : ''}`} />
                    <span className="text-sm font-medium text-gray-700">Veeqo Sync:</span>
                </div>

                <div className="flex-1 min-w-0">
                    {showProgress ? (
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-blue-700 shrink-0">
                                Syncing {progress.synced}/{progress.total} products
                            </span>
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                <div
                                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                            <span className="text-xs text-gray-500 shrink-0">{progressPct}%</span>
                        </div>
                    ) : status ? (
                        <div className="flex items-center space-x-4 text-xs">
                            {status.waiting > 0 && (
                                <div className="flex items-center text-gray-600">
                                    <Clock className="h-3 w-3 mr-1" />
                                    <span>{status.waiting} pending</span>
                                </div>
                            )}
                            {status.active > 0 && (
                                <div className="flex items-center text-blue-600">
                                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                    <span>Syncing...</span>
                                </div>
                            )}
                            {status.failed > 0 && (
                                <div className="flex items-center text-red-600 font-medium">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    <span>{status.failed} failed</span>
                                </div>
                            )}
                            {isIdle && !syncing && (
                                <div className="flex items-center text-green-600">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    <span>All up to date</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <span className="text-xs text-red-500 italic">Status unavailable</span>
                    )}
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    disabled={syncing || isSyncActive}
                    onClick={handleManualSync}
                    className="ml-auto text-xs h-8 shrink-0"
                >
                    {syncing || isSyncActive ? 'Syncing...' : `Sync ${type.charAt(0).toUpperCase() + type.slice(1)} Now`}
                </Button>
            </div>

            {/* Progress bar at the bottom edge when syncing */}
            {showProgress && (
                <div className="h-0.5 bg-gray-100">
                    <div
                        className="bg-blue-500 h-0.5 transition-all duration-500"
                        style={{ width: `${progressPct}%` }}
                    />
                </div>
            )}
        </div>
    );
};

export default VeeqoSyncStatus;
